import "./GlobalSearch.css";

import { useEffect, useMemo, useRef, useState } from "react";

import { CountryFlag } from "../CountryFlag/CountryFlag";
import PropTypes from "prop-types";
import { THEME } from "../../constants";
import chroma from "chroma-js";
import { requestGlobalSearch } from "../../services/backendRequests";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
// How many rows each section shows before the "see more" toggle.
const PREVIEW_LIMIT = 5;
// How many sites are shown inside a single campaign group before collapsing to a link.
const SITES_PER_GROUP = 4;
const EMPTY_RESULTS = {
  campaigns: [],
  campaigns_total: 0,
  sites: [],
  site_counts: {},
  sites_capped: false,
  companies: [],
  companies_total: 0,
};

// Bolds the first occurrence of `query` inside `text` (case-insensitive).
const Highlight = ({ text, query }) => {
  if (!text) return null;
  const idx = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
};

Highlight.propTypes = {
  text: PropTypes.string,
  query: PropTypes.string,
};

// Builds the `campaign -> sites` tree from the API's flat lists. A campaign
// appears if it matched directly or if any of its sites matched. `sites` holds
// only the sampled sites the backend returned; `matched_sites` is the real total
// matching count for that campaign, used to drive the "+N more" link.
const buildTree = (results) => {
  if (!results) return [];
  const counts = results.site_counts ?? {};
  const byId = new Map();
  (results.campaigns ?? []).forEach((campaign) => {
    byId.set(campaign.id, {
      id: campaign.id,
      name: campaign.name,
      company_name: campaign.company_name,
      matched_sites: counts[campaign.id] ?? 0,
      sites: [],
    });
  });
  (results.sites ?? []).forEach((site) => {
    if (!byId.has(site.contract_info_id))
      byId.set(site.contract_info_id, {
        id: site.contract_info_id,
        name: site.campaign_name,
        company_name: site.company_name,
        matched_sites: counts[site.contract_info_id] ?? 0,
        sites: [],
      });

    byId.get(site.contract_info_id).sites.push(site);
  });
  return Array.from(byId.values());
};

const accentFor = (companyName) => {
  if (!companyName) return "#81b23d";
  return chroma(THEME.companyColorHash.hex(companyName)).hex();
};

export const GlobalSearch = () => {
  const user = useSelector(({ user }) => user);
  const history = useHistory();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const [showAllCompanies, setShowAllCompanies] = useState(false);

  const containerRef = useRef(null);
  const requestIdRef = useRef(0);

  // Debounced fetch with stale-response guard.
  useEffect(() => {
    const trimmed = query.trim();
    setShowAllCampaigns(false);
    setShowAllCompanies(false);
    if (trimmed.length < MIN_CHARS) {
      setResults(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      requestGlobalSearch(user, trimmed)
        .then((data) => {
          if (requestId === requestIdRef.current) {
            setResults(data);
            setLoading(false);
          }
        })
        .catch(() => {
          if (requestId === requestIdRef.current) {
            setResults(EMPTY_RESULTS);
            setLoading(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, user]);

  // Close the panel when clicking outside.
  useEffect(() => {
    const onMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const campaignTree = useMemo(() => buildTree(results), [results]);
  const companies = results?.companies ?? [];
  const hasResults = campaignTree.length > 0 || companies.length > 0;
  const showPanel = open && query.trim().length >= MIN_CHARS;

  const visibleCampaigns = showAllCampaigns
    ? campaignTree
    : campaignTree.slice(0, PREVIEW_LIMIT);
  const hiddenCampaigns = campaignTree.length - visibleCampaigns.length;
  // True when the backend capped campaigns or sites (more exist than were fetched).
  const campaignsCapped =
    (results?.campaigns_total ?? 0) > (results?.campaigns?.length ?? 0) ||
    Boolean(results?.sites_capped);

  const visibleCompanies = showAllCompanies
    ? companies
    : companies.slice(0, PREVIEW_LIMIT);
  const hiddenCompanies = companies.length - visibleCompanies.length;
  const companiesCapped = (results?.companies_total ?? 0) > companies.length;

  const select = (path) => {
    setOpen(false);
    history.push(path);
  };

  return (
    <div className="gsearch" ref={containerRef}>
      <div className="gsearch__input">
        <i className="fa-solid fa-magnifying-glass gsearch__icon" />
        <input
          type="text"
          placeholder="Search campaigns, sites, companies…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
        />
        {/* Same clear cross as the app's other search boxes (_filters.scss). */}
        {query && (
          <button
            type="button"
            className="cf-search__clear"
            onClick={() => setQuery("")}
            aria-label="Clear search"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {showPanel && (
        <div className="gsearch__panel">
          {loading ? (
            <div className="gsearch__skeleton">
              <span />
              <span />
              <span />
            </div>
          ) : !hasResults ? (
            <div className="gsearch__empty">No results</div>
          ) : (
            <>
              {campaignTree.length > 0 && (
                <div className="gsearch__section">
                  <div className="gsearch__section-title">
                    Campaigns & sites
                  </div>
                  {visibleCampaigns.map((campaign) => {
                    const shown = campaign.sites.slice(0, SITES_PER_GROUP);
                    return (
                      <div key={campaign.id} className="gsearch__group">
                        <button
                          type="button"
                          className="gsearch__row gsearch__row--campaign"
                          onClick={() => select(`/campaigns/${campaign.id}`)}
                        >
                          <span
                            className="gsearch__dot"
                            style={{
                              backgroundColor: accentFor(campaign.company_name),
                            }}
                          />
                          <span className="gsearch__label">
                            <Highlight
                              text={campaign.name}
                              query={query.trim()}
                            />
                          </span>
                        </button>
                        {shown.map((site) => (
                          <button
                            key={site.id}
                            type="button"
                            className="gsearch__row gsearch__row--site"
                            onClick={() =>
                              select(
                                `/campaigns/${site.contract_info_id}/sites/${site.id}`
                              )
                            }
                          >
                            {site.country ? (
                              <CountryFlag size="sm" alpha={site.country} />
                            ) : (
                              <i className="fa-solid fa-location-dot gsearch__site-icon" />
                            )}
                            <span className="gsearch__label">
                              <Highlight
                                text={site.display_name || site.name}
                                query={query.trim()}
                              />
                            </span>
                          </button>
                        ))}
                        {campaign.matched_sites > shown.length && (
                          <button
                            type="button"
                            className="gsearch__more gsearch__more--sites"
                            onClick={() => select(`/campaigns/${campaign.id}`)}
                          >
                            +{campaign.matched_sites - shown.length} more · open
                            campaign <i className="fa-solid fa-arrow-right" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {hiddenCampaigns > 0 && (
                    <button
                      type="button"
                      className="gsearch__more"
                      onClick={() => setShowAllCampaigns(true)}
                    >
                      See more ({hiddenCampaigns})
                    </button>
                  )}
                  {showAllCampaigns && campaignsCapped && (
                    <div className="gsearch__refine">
                      Refine your search to see more
                    </div>
                  )}
                </div>
              )}

              {companies.length > 0 && (
                <div className="gsearch__section">
                  <div className="gsearch__section-title">Companies</div>
                  {visibleCompanies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      className="gsearch__row gsearch__row--company"
                      onClick={() => select(`/campaigns?company=${company.id}`)}
                    >
                      <span className="gsearch__label">
                        <Highlight text={company.name} query={query.trim()} />
                      </span>
                    </button>
                  ))}
                  {hiddenCompanies > 0 && (
                    <button
                      type="button"
                      className="gsearch__more"
                      onClick={() => setShowAllCompanies(true)}
                    >
                      See more ({hiddenCompanies})
                    </button>
                  )}
                  {showAllCompanies && companiesCapped && (
                    <div className="gsearch__refine">
                      Refine your search to see more
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
